// WYSIWYG copy for the AI Assistant transcript — Ctrl+C should put exactly what the user sees
// on screen into the clipboard as plain text, never raw [[type:len:payload]] wire markers or JSON.
//
// Important: users often keep focus in the MessageComposer (ProseMirror) while selecting answer
// text above it. The copy event targets the focused editor, so the handler must run in the
// capture phase on a panel ancestor and stop propagation before ProseMirror overwrites the
// clipboard with an empty slice.
import {
    CITATIONS_MARKER_REGEX,
    parseCitationMarkerPayload,
} from '../components/messageRows/markdown/CitationsMarkdownComponents';
import {
    CITE_MARKER_REGEX,
    resolveCiteMarkerItems,
} from '../agenticRun/components/markdown/agenticMarkdownMarkers';
import {
    decodeInlineComponentsToSegments,
    entityDisplayLabel,
    polishInlineSegmentsForDisplay,
    stripTechnicalEntityRefs,
    userDisplayLabel,
} from '../services/inlineComponents/decodeInlineComponentStream';

function isNodeWithinContainer(node, container) {
    if (!node || !container) {
        return false;
    }
    if (node === container) {
        return true;
    }
    if (node.nodeType === Node.TEXT_NODE) {
        return container.contains(node);
    }
    return container.contains(node);
}

function hasMeaningfulCopyText(text) {
    return Boolean(String(text || '').replace(/\u00a0/g, ' ').trim());
}

function citationCountLabel(count) {
    if (!count) {
        return '';
    }
    return `${count} Citation${count === 1 ? '' : 's'}`;
}

function replaceLegacyCitationMarkersForCopy(text) {
    return String(text || '').replace(new RegExp(CITATIONS_MARKER_REGEX), (_match, rawPayload) => {
        const parsed = parseCitationMarkerPayload(rawPayload);
        return citationCountLabel(parsed?.items?.length || 0) || _match;
    });
}

function replaceCiteMarkersForCopy(text, citations) {
    return String(text || '').replace(new RegExp(CITE_MARKER_REGEX), (match, idsText) => {
        const items = resolveCiteMarkerItems(idsText, citations);
        return citationCountLabel(items.length) || match;
    });
}

function inlineComponentDisplayLabel(type, payload) {
    if (!payload || typeof payload !== 'object') {
        return type === 'entity' || type === 'user' ? '' : String(type || '');
    }

    const label = payload.title || payload.name || payload.label || payload.reportTitle;
    if (label) {
        return String(label);
    }
    if (type === 'citation' && payload.reportId) {
        return 'FINTEL';
    }
    if (type === 'user') {
        return userDisplayLabel(payload) || '';
    }
    if (type === 'entity') {
        return entityDisplayLabel(payload) || '';
    }
    if (type !== 'entity') {
        const id = payload.id;
        if (id) {
            return String(id);
        }
    }
    return type === 'entity' || type === 'user' ? '' : String(type || '');
}

function segmentsToPlainCopyText(segments, citations) {
    let result = '';
    for (const segment of segments) {
        if (segment.type === 'text') {
            let text = segment.value;
            text = replaceLegacyCitationMarkersForCopy(text);
            text = replaceCiteMarkersForCopy(text, citations);
            result += text;
        } else {
            const label = inlineComponentDisplayLabel(segment.type, segment.payload);
            if (label) {
                result += label;
            }
        }
    }
    return result;
}

function normalizeCopyLineBreaks(text) {
    return String(text || '')
        .replace(/\r\n/g, '\n')
        .replace(/\u00a0/g, ' ')
        .replace(/\n{3,}/g, '\n\n')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n[ \t]+/g, '\n')
        .trimEnd();
}

/**
 * Converts assistant markdown / wire markers into the plain text a user sees in the transcript.
 * Idempotent on already-polished copy text.
 *
 * @param {string} text
 * @param {{citations?: Array}} [options]
 */
export function cleanAiAssistantCopyText(text, {citations = []} = {}) {
    if (!hasMeaningfulCopyText(text)) {
        return '';
    }

    if (!String(text).includes('[[')) {
        return normalizeCopyLineBreaks(stripTechnicalEntityRefs(text));
    }

    const {segments} = decodeInlineComponentsToSegments(text);
    const displaySegments = polishInlineSegmentsForDisplay(segments);
    const rebuilt = segmentsToPlainCopyText(displaySegments, citations);
    return normalizeCopyLineBreaks(stripTechnicalEntityRefs(rebuilt));
}

// Block tags that should become line breaks in copied plain text. Omit generic DIV —
// the transcript is wrapped in layout divs that would otherwise inject extra blanks.
const COPY_BLOCK_TAGS = new Set([
    'P', 'LI', 'TR', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
    'BLOCKQUOTE', 'PRE', 'SECTION', 'ARTICLE', 'BR', 'DT', 'DD',
]);

function serializeClonedFragmentAsPlainText(root) {
    let result = '';

    function walk(node) {
        if (node.nodeType === Node.TEXT_NODE) {
            result += node.nodeValue;
            return;
        }
        if (node.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
            for (let child = node.firstChild; child; child = child.nextSibling) {
                walk(child);
            }
            return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) {
            return;
        }
        if (node.nodeName === 'BR') {
            result += '\n';
            return;
        }
        for (let child = node.firstChild; child; child = child.nextSibling) {
            walk(child);
        }
        if (COPY_BLOCK_TAGS.has(node.nodeName)) {
            result += '\n';
        }
    }

    walk(root);
    return result;
}

function collapsedComparableText(text) {
    return String(text || '').replace(/\s+/g, '');
}

function readPlainTextFromRange(range, selection) {
    const nativeText = selection.toString();
    const fragment = range.cloneContents();
    const serialized = serializeClonedFragmentAsPlainText(fragment);

    // Chromium omits text inside draggable / user-select:none nodes from Selection.toString()
    // and the native clipboard, even when the highlight covers them. Entity chips in the
    // assistant are HTML5-draggable onto the chart, so prefer the cloned DOM when it has
    // visible labels that the native selection dropped.
    if (
        hasMeaningfulCopyText(serialized)
        && collapsedComparableText(serialized).length > collapsedComparableText(nativeText).length
    ) {
        return serialized;
    }
    if (hasMeaningfulCopyText(nativeText)) {
        return nativeText;
    }
    return hasMeaningfulCopyText(serialized) ? serialized : '';
}

/**
 * Reads the current DOM selection as layout-faithful plain text, scoped to `container`.
 * Returns null when there is no meaningful non-collapsed selection inside the container.
 *
 * @param {HTMLElement} container
 */
export function getWysiwygPlainTextFromSelection(container) {
    const selection = window.getSelection?.();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed || !container) {
        return null;
    }

    const range = selection.getRangeAt(0);
    if (!isNodeWithinContainer(range.commonAncestorContainer, container)) {
        return null;
    }

    const plainText = readPlainTextFromRange(range, selection);
    return hasMeaningfulCopyText(plainText) ? plainText : null;
}

/**
 * @param {ClipboardEvent} event
 * @param {HTMLElement | null | undefined} container transcript root element
 * @param {{citations?: Array, stopPropagation?: boolean}} [options]
 */
export function handleAiAssistantCopyEvent(event, container, options = {}) {
    if (!event?.clipboardData || !container) {
        return false;
    }

    const selectedText = getWysiwygPlainTextFromSelection(container);
    if (selectedText == null) {
        return false;
    }

    const cleaned = cleanAiAssistantCopyText(selectedText, options);
    if (!hasMeaningfulCopyText(cleaned)) {
        return false;
    }

    event.clipboardData.setData('text/plain', cleaned);
    event.preventDefault();
    if (options.stopPropagation) {
        event.stopPropagation();
    }
    return true;
}

export default handleAiAssistantCopyEvent;
